import { useEffect, useState } from "react";

import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";

type TutorialStage =
  | "ready"
  | "inserted"
  | "port-only"
  | "independent-sides"
  | "symmetric"
  | "antisymmetric"
  | "complete";

interface TutorialCopy {
  readonly description: string;
  readonly nextLabel: string;
  readonly progress: number;
  readonly title: string;
}

const TUTORIAL_COPY: Record<Exclude<TutorialStage, "ready">, TutorialCopy> = {
  inserted: {
    title: "Separate geometry, ready to move",
    description: "Loom inserted one fixed wing body, separate movable bodies for port and starboard, and a straight hinge on each side.",
    nextLabel: "Move port +12°",
    progress: 1,
  },
  "port-only": {
    title: "Port moved; starboard stayed neutral",
    description: "The port body rotated about its cached hinge. Starboard and the fixed wing did not move or rebuild.",
    nextLabel: "Move starboard −8°",
    progress: 2,
  },
  "independent-sides": {
    title: "Two explicit side states",
    description: "Port remains +12° while starboard moves to −8°. Both values are stored independently in the pose receipt.",
    nextLabel: "Compare symmetric +10°",
    progress: 3,
  },
  symmetric: {
    title: "Symmetric command, honest resolved sides",
    description: "A +10° command resolves to local trailing-edge-down +10° on both port and starboard.",
    nextLabel: "Compare antisymmetric +10°",
    progress: 4,
  },
  antisymmetric: {
    title: "Antisymmetric command",
    description: "A +10° command resolves semantically to port −10° and starboard +10°, independent of mirrored world-axis orientation.",
    nextLabel: "Finish tutorial",
    progress: 5,
  },
  complete: {
    title: "Independent actuation demonstrated",
    description: "The same authored control moved through independent and coupled poses while its definition and fixed-wing topology stayed unchanged.",
    nextLabel: "Replay from neutral",
    progress: 5,
  },
};

export function LoomControlSurfaceTutorial() {
  const controlSurface = useLoomControlSurfaceContext();
  const [stage, setStage] = useState<TutorialStage>("ready");
  const [starting, setStarting] = useState(false);
  const selectedControlId = controlSurface?.selectedControl?.id ?? null;
  const selectedState = controlSurface?.selectedState ?? null;

  useEffect(() => {
    if (!starting || !controlSurface || !selectedControlId || !selectedState) return;
    controlSurface.setResolvedDeflections("independent", 0, 0);
    setStage("inserted");
    setStarting(false);
  }, [controlSurface, selectedControlId, selectedState, starting]);

  if (!controlSurface?.selectedWing) return null;
  const hasControl = Boolean(controlSurface.collection?.controlSurfaces.length);
  const canStart = hasControl || (controlSurface.canAddControlSurface
    && (controlSurface.collection?.controlSurfaces.length ?? 0) < 16);

  const start = async () => {
    setStarting(true);
    const existing = controlSurface.collection?.controlSurfaces[0];
    if (existing) controlSurface.selectControlSurface(existing.id);
    else await controlSurface.addControlSurface();
  };

  const selectSide = (side: "port" | "starboard") => {
    if (!selectedControlId) return;
    controlSurface.selectBody(`${controlSurface.selectedWing!.id}:control:${selectedControlId}:${side}`);
  };

  const advance = () => {
    if (!selectedControlId || !selectedState) return;
    if (stage === "inserted") {
      selectSide("port");
      controlSurface.setResolvedDeflections("independent", 12, 0);
      setStage("port-only");
    } else if (stage === "port-only") {
      selectSide("starboard");
      controlSurface.setResolvedDeflections("independent", 12, -8);
      setStage("independent-sides");
    } else if (stage === "independent-sides") {
      controlSurface.setResolvedDeflections("symmetric", 10, 10);
      setStage("symmetric");
    } else if (stage === "symmetric") {
      controlSurface.setResolvedDeflections("antisymmetric", -10, 10);
      setStage("antisymmetric");
    } else if (stage === "antisymmetric") {
      setStage("complete");
    } else if (stage === "complete") {
      controlSurface.setResolvedDeflections("independent", 0, 0);
      setStage("inserted");
    }
  };

  return <section
    aria-label="Independent control actuation tutorial"
    className="loom-control-tutorial"
    data-stage={stage}
    data-testid="loom-control-surface-tutorial"
  >
    <header>
      <div><span>Guided demo</span><strong>Independent control actuation</strong></div>
      {stage !== "ready" ? <button
        className="is-quiet"
        data-testid="loom-control-tutorial-close"
        onClick={() => setStage("ready")}
        type="button"
      >Close</button> : null}
    </header>
    {stage === "ready" ? <div className="loom-control-tutorial-intro">
      <p>Insert a plain trailing-edge control, move each side alone, then compare symmetric and antisymmetric coupling on the same bodies.</p>
      <button
        data-testid="loom-control-tutorial-start"
        disabled={!canStart || starting}
        onClick={() => void start()}
        type="button"
      >{starting ? "Preparing demo…" : hasControl ? "Run demo on selected wing" : "Insert control & start"}</button>
    </div> : (() => {
      const copy = TUTORIAL_COPY[stage];
      return <div className="loom-control-tutorial-active">
        <div
          aria-label={`Tutorial step ${copy.progress} of 5`}
          aria-valuemax={5}
          aria-valuemin={1}
          aria-valuenow={copy.progress}
          className="loom-control-tutorial-progress"
          role="progressbar"
        >{Array.from({ length: 5 }, (_, index) => <i
          className={index < copy.progress ? "is-complete" : undefined}
          key={index}
        />)}</div>
        <div aria-live="polite" data-testid="loom-control-tutorial-step">
          <span>Step {copy.progress} of 5</span>
          <strong>{copy.title}</strong>
          <p>{copy.description}</p>
        </div>
        <output
          data-mode={selectedState?.mode ?? "unavailable"}
          data-port-angle-deg={selectedState?.portDeflectionDeg ?? "unavailable"}
          data-starboard-angle-deg={selectedState?.starboardDeflectionDeg ?? "unavailable"}
          data-testid="loom-control-tutorial-receipt"
        >Port {selectedState?.portDeflectionDeg.toFixed(1) ?? "—"}° · Starboard {selectedState?.starboardDeflectionDeg.toFixed(1) ?? "—"}°</output>
        <button
          data-testid="loom-control-tutorial-next"
          disabled={!selectedControlId || !selectedState}
          onClick={advance}
          type="button"
        >{copy.nextLabel}</button>
      </div>;
    })()}
  </section>;
}
